import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import Modal from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/modals.css';
import './ForgotPasswordModal.css';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin, isStandalonePage = false }) => {
  const { forgotPassword } = useAuth();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      setStatus(null);
      await forgotPassword(values.email);
      setSubmittedEmail(values.email);
      setIsSubmitted(true);
    } catch (error) {
      // For security reasons, we show a generic success message even if the email doesn't exist
      // This prevents user enumeration attacks
      setSubmittedEmail(values.email);
      setIsSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setIsSubmitted(false);
    setSubmittedEmail('');
  };

  // If using standalone page mode, render with page layout instead of modal
  if (isStandalonePage) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-modal">
          <div className="auth-page-content">
            {isSubmitted ? (
              <ForgotPasswordSuccess 
                submittedEmail={submittedEmail}
                onTryAgain={handleTryAgain}
                onBackToLogin={onBackToLogin}
                isStandalonePage={isStandalonePage}
              />
            ) : (
              <>
                <h2>Forgot Password</h2>
                <ForgotPasswordForm onSubmit={handleSubmit} onBackToLogin={onBackToLogin} />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Otherwise use the Modal component
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={isSubmitted ? "Check Your Email" : "Forgot Password"}
      size="medium"
      closeOnBackdrop={true}
      closeOnEsc={true}
    >
      {isSubmitted ? (
        <ForgotPasswordSuccess 
          submittedEmail={submittedEmail}
          onTryAgain={handleTryAgain}
          onBackToLogin={onBackToLogin}
          isStandalonePage={isStandalonePage}
        />
      ) : (
        <ForgotPasswordForm onSubmit={handleSubmit} onBackToLogin={onBackToLogin} />
      )}
    </Modal>
  );
};

// Extract form to separate component for reuse
const ForgotPasswordForm = ({ onSubmit, onBackToLogin }) => {
  const initialValues = {
    email: ''
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required')
  });

  return (
    <>
      <p className="form-description">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form>
            {status && status.error && (
              <div className="alert alert-danger">{status.error}</div>
            )}
          
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <Field
                type="email"
                name="email"
                id="email"
                className="form-control"
                placeholder="Enter your email address"
                aria-describedby="email-error"
              />
              <ErrorMessage 
                name="email" 
                component="div" 
                className="error-message"
                id="email-error"
                role="alert"
              />
            </div>
          
            <button 
              type="submit" 
              disabled={isSubmitting} 
              className="btn btn-primary"
              aria-describedby={isSubmitting ? "loading-text" : undefined}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner" aria-hidden="true"></span>
                  <span id="loading-text">Sending Reset Link...</span>
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          
            <div className="form-footer">
              <p>
                Remember your password?{' '}
                {onBackToLogin ? (
                  <button type="button" className="link-button" onClick={onBackToLogin}>
                    Back to Login
                  </button>
                ) : (
                  <Link to="/login" className="link-button">
                    Back to Login
                  </Link>
                )}
              </p>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

// Extract success message to separate component for reuse
const ForgotPasswordSuccess = ({ submittedEmail, onTryAgain, onBackToLogin, isStandalonePage }) => {
  return (
    <>
      <div className="alert alert-success">
        <p>
          If an account with the email address <strong>{submittedEmail}</strong> exists, 
          you will receive a password reset link shortly.
        </p>
        <p>
          Please check your email and follow the instructions to reset your password.
        </p>
      </div>

      <div className="form-footer">
        <p>
          Didn't receive an email? Check your spam folder or{' '}
          <button 
            type="button" 
            className="link-button" 
            onClick={onTryAgain}
          >
            try again
          </button>
        </p>
        <p>
          <button
            type="button"
            className="link-button"
            onClick={onBackToLogin}
          >
            ← Back to Login
          </button>
        </p>
      </div>
    </>
  );
};

export default ForgotPasswordModal;